import type { PrototypeState } from '../controllerState';
import type { Root } from '../prototypeTypes';
import { PrototypeApiError } from '../apiClient';
import { closeModal, closeModalStackForRoute, openModal, showView } from '../navigationController';
import { showToast } from '../toastController';
import { recordRuntimeError } from '../prototypeRuntimeSupport';
import { safeExternalHttpsUrl } from '../../../domain/externalUrlPolicy';
import { restartModalTurnstile } from '../modalTurnstileLifecycle';
import { setAuthButtonLoading } from '../actions/authButtonLock';
import { clearUpgradeConfirmPrompt } from '../actions/billingUpgradeConfirmPrompt';
import { clearUpgradeResumePrompt, currentUpgradeResumePromptAttempt, ownsUpgradeResumePrompt } from '../actions/billingUpgradeResumePrompt';
import { activateFeedbackTab, switchToFeedbackTab } from './profileFeedbackTicketActions';
import { clearOperationFeedback, paintOperationFeedback } from '../operationFeedback';
import { ensureBusinessOverlayMounted } from '../businessOverlayLifecycle';

type AutoRenewOutcome =
	| { ok: true }
	| { ok: false; message: string };

const cancelAutoRenewAttempts = new WeakMap<Root, symbol>();

export function togglePrivacyPanel(root: Root): void {
	const panel = root.querySelector<HTMLElement>('#privacyPanel');
	if (!panel) return;
	panel.hidden = !panel.hidden;
	root.querySelector<HTMLElement>('#profilePrivacyRow')?.setAttribute('aria-expanded', String(!panel.hidden));
}

export async function openBillingPortal(root: Root, state: PrototypeState): Promise<void> {
	try {
		const subscriptionId = await resolveCurrentProviderSubscriptionId(state);
		const res = await state.runtime.api.createBillingPortal(subscriptionId) as { portalUrl?: string };
		const portalUrl = safeExternalHttpsUrl(res.portalUrl);
		if (portalUrl) window.open(portalUrl, '_blank', 'noopener');
		else showToast({ type: 'error', message: 'Billing portal is unavailable right now.' });
	} catch (error) {
		recordRuntimeError(state.runtime, error);
		showToast({ type: 'error', message: 'Billing portal is unavailable right now.' });
	}
}

export async function openCancelAutoRenewModal(root: Root, state: PrototypeState): Promise<void> {
	void state;
	ensureBusinessOverlayMounted(root, 'cancelAutoRenewModal');
	cancelAutoRenewAttempts.set(root, Symbol('cancel-auto-renew'));
	clearOperationFeedback(root, 'cancelAutoRenewFeedback');
	const confirmBtn = root.querySelector<HTMLButtonElement>('#confirmCancelAutoRenewBtn');
	if (confirmBtn) {
		confirmBtn.disabled = true;
		confirmBtn.setAttribute('aria-disabled', 'true');
	}
	const overlay = root.querySelector<HTMLElement>('#cancelAutoRenewModal');
	if (overlay) overlay.hidden = false;
	openModal(root, 'cancelAutoRenewModal');
}

export function closeCancelAutoRenewModal(root: Root): void {
	cancelAutoRenewAttempts.delete(root);
	closeModal(root, 'cancelAutoRenewModal');
	const overlay = root.querySelector<HTMLElement>('#cancelAutoRenewModal');
	if (overlay) overlay.hidden = true;
}

export async function confirmCancelAutoRenew(root: Root, state: PrototypeState): Promise<void> {
	const attempt = cancelAutoRenewAttempts.get(root) ?? Symbol('cancel-auto-renew');
	cancelAutoRenewAttempts.set(root, attempt);
	const ownsAttempt = () => cancelAutoRenewAttempts.get(root) === attempt
		&& root.querySelector<HTMLElement>('#cancelAutoRenewModal')?.classList.contains('active') === true;
	const container = root.querySelector<HTMLElement>('#cancelAutoRenewTurnstileContainer');
	if (!container) {
		paintOperationFeedback(root, 'cancelAutoRenewFeedback', 'Verification is not available. Please reopen this dialog.');
		return;
	}
	const confirmButton = root.querySelector<HTMLButtonElement>('#confirmCancelAutoRenewBtn');
	if (!confirmButton) {
		paintOperationFeedback(root, 'cancelAutoRenewFeedback', 'Cancel control is not available. Please reopen this dialog.');
		return;
	}
	if (confirmButton.dataset.submitting === 'true') return;
	const turnstileToken = container.dataset.turnstileToken;
	if (!turnstileToken) {
		paintOperationFeedback(root, 'cancelAutoRenewFeedback', 'Please complete the verification.');
		return;
	}
	clearOperationFeedback(root, 'cancelAutoRenewFeedback');
	delete container.dataset.turnstileToken;
	confirmButton.dataset.submitting = 'true';
	confirmButton.setAttribute('aria-busy', 'true');
	setAuthButtonLoading(confirmButton, true, 'Confirm Cancel', 'Cancelling…');
	try {
		const outcome = await changeAutoRenew(root, state, false, turnstileToken);
		if (!ownsAttempt()) return;
		if (outcome.ok) {
			closeCancelAutoRenewModal(root);
			showToast({ type: 'success', message: 'Auto-renew turned off. Access continues until the period ends.' });
		} else {
			paintOperationFeedback(root, 'cancelAutoRenewFeedback', outcome.message);
			await restartModalTurnstile(root, 'cancelAutoRenewModal', state.runtime);
		}
	} finally {
		if (ownsAttempt()) {
			delete confirmButton.dataset.submitting;
			confirmButton.removeAttribute('aria-busy');
			// Restores the spinner/text and re-enables only if Turnstile has not
			// re-locked the button (restartModalTurnstile runs before this and locks
			// it for the fresh challenge, so it stays visibly disabled).
			setAuthButtonLoading(confirmButton, false);
		}
	}
}

export async function changeAutoRenew(root: Root, state: PrototypeState, enabled: boolean, turnstileToken?: string): Promise<AutoRenewOutcome> {
	if (!enabled && !turnstileToken) {
		return { ok: false, message: 'Verification required. Please try again.' };
	}
	try {
		const subscription = await state.runtime.api.getCurrentSubscription();
		const subscriptionId = subscription.subscription?.providerSubscriptionId;
		const periodEnd = subscription.subscription?.currentPeriodEnd;
		if (!subscriptionId || !periodEnd) {
			return { ok: false, message: 'No active paid subscription is available to update.' };
		}
		await state.runtime.api.setAutoRenew(enabled, periodEnd, subscriptionId, turnstileToken);
	} catch (error) {
		recordRuntimeError(state.runtime, error);
		if (error instanceof PrototypeApiError && (error.status === 401 || error.status === 403)) {
			const code = typeof (error.body as Record<string, unknown> | undefined)?.error === 'string'
				? String((error.body as Record<string, unknown>).error) : '';
			if (code === 'turnstile_required' || code === 'turnstile_failed') {
				return { ok: false, message: 'Verification failed. Please try again.' };
			}
		}
		return { ok: false, message: 'Could not update billing. Please try again.' };
	}
	try {
		const { hydrateProfile } = await import('../profileHydrator');
		await hydrateProfile(root, state.runtime.api, { isLoggedIn: state.isLoggedIn });
		const membershipView = root.querySelector<HTMLElement>('#view-membership-billing');
		if (membershipView?.classList.contains('active')) {
			const { hydrateMembershipBilling } = await import('../membershipBillingHydrator');
			await hydrateMembershipBilling(root, state.runtime.api);
		}
	} catch (error) {
		recordRuntimeError(state.runtime, error);
	}
	return { ok: true };
}

export async function resumeAutoRenew(root: Root, state: PrototypeState, source: HTMLButtonElement): Promise<boolean> {
	if (source.dataset.submitting === 'true') return false;
	source.dataset.submitting = 'true';
	source.disabled = true;
	source.setAttribute('aria-busy', 'true');
	try {
		const outcome = await changeAutoRenew(root, state, true);
		if (outcome.ok) showToast({ type: 'success', message: 'Auto-renew resumed.' });
		else showToast({ type: 'error', message: outcome.message });
		return outcome.ok;
	} finally {
		delete source.dataset.submitting;
		source.disabled = false;
		source.removeAttribute('aria-busy');
	}
}

/**
 * Re-open auto-renew from the upgrade-resume prompt (subscription_resume_required).
 * Auto-renew enable needs no Turnstile. On success close the prompt so the user
 * can upgrade again; on failure swap the prompt body to the support/feedback box.
 */
export async function resumeUpgradeRenew(root: Root, state: PrototypeState): Promise<void> {
	const button = root.querySelector<HTMLButtonElement>('#resumeUpgradeRenewBtn');
	if (!button || button.dataset.submitting === 'true') return;
	button.dataset.submitting = 'true';
	button.disabled = true;
	button.setAttribute('aria-busy', 'true');
	const attempt = currentUpgradeResumePromptAttempt(root);
	let outcome: AutoRenewOutcome = { ok: false, message: 'Could not update billing. Please try again.' };
	try {
		outcome = await changeAutoRenew(root, state, true);
	} finally {
		if (ownsUpgradeResumePrompt(root, attempt)) {
			delete button.dataset.submitting;
			button.disabled = false;
			button.removeAttribute('aria-busy');
		}
	}
	if (!ownsUpgradeResumePrompt(root, attempt)) return;
	if (outcome.ok) {
		closeUpgradeResumeModal(root);
		showToast({ type: 'success', message: 'Auto-renew is back on. You can now upgrade.' });
	} else {
		showUpgradeResumeSupport(root);
	}
}

export function closeUpgradeResumeModal(root: Root): void {
	clearUpgradeResumePrompt(root);
	closeModal(root, 'upgradeResumeModal');
	const overlay = root.querySelector<HTMLElement>('#upgradeResumeModal');
	if (overlay) overlay.hidden = true;
}

export async function openFeedbackFromUpgradeResume(root: Root, state: PrototypeState): Promise<void> {
	void state;
	clearUpgradeConfirmPrompt(root);
	await closeModalStackForRoute(root, ['upgradeResumeModal', 'upgradeConfirmModal', 'paywallModal']);
	showView('feedback', root);
	switchToFeedbackTab(root, 'send');
	activateFeedbackTab(root, 'send');
}

function showUpgradeResumeSupport(root: Root): void {
	const promptBody = root.querySelector<HTMLElement>('#upgradeResumePromptBody');
	const supportBody = root.querySelector<HTMLElement>('#upgradeResumeSupportBody');
	if (promptBody) promptBody.hidden = true;
	if (supportBody) {
		// Modal entry resets data-reset-view="hide" sections with both the
		// hidden attribute and an inline display:none. Clearing only hidden here
		// leaves the fallback body invisible after a failed resume attempt.
		supportBody.hidden = false;
		supportBody.style.display = '';
	}
}

async function resolveCurrentProviderSubscriptionId(state: PrototypeState): Promise<string | undefined> {
	const current = await state.runtime.api.getCurrentSubscription();
	return current.subscription?.providerSubscriptionId || undefined;
}
